import { create } from 'zustand';

// ── Contexto de navegação em tempo real — o Sistema Marcianus "conhece" onde o
// utilizador está (tab/subtab) para o Hexis dar recomendações assertivas e para
// captura de dados comportamentais estratégicos. Atualizado pelo AppShell.

export interface NavContext {
  tab: string;      // eventos | tap | centro
  subtab: string;   // ex.: 'proximos', 'scan', 'vendas'
  path: string;     // pathname + search (para telemetria)
  label: string;    // legível: "Eventos › Próximos"
}

interface NavState extends NavContext {
  history: string[];                 // últimas rotas (comportamento)
  set: (ctx: NavContext) => void;
}

export const useNav = create<NavState>((set) => ({
  tab: 'eventos', subtab: '', path: '/eventos', label: 'Eventos', history: [],
  set: (ctx) =>
    set((s) => ({
      ...ctx,
      history: s.path === ctx.path ? s.history : [...s.history, ctx.path].slice(-20),
    })),
}));

const TAB_LABEL: Record<string, string> = { eventos: 'Eventos', tap: 'Tap', descobrir: 'Descobrir' };
const SUB_LABEL: Record<string, string> = {
  proximos: 'Próximos', passados: 'Passados', meus: 'Meus', todos: 'Todos',
  myqr: 'Meu QR', scan: 'Scan', taps: 'Taps',
  vendas: 'Vendas', checkin: 'Check-in', eventos: 'Eventos', conteudo: 'Conteúdos',
  equipa: 'Equipa', billing: 'Plano',
};

// Rotas com nome próprio (para o Hexis saber a tela EXATA em tempo real).
const PATH_LABEL: Record<string, string> = {
  '/conta': 'A minha conta',
  '/bilhetes': 'Os meus bilhetes',
  '/carteira': 'Carteira',
  '/conteudos': 'Conteúdos',
  '/marketplace': 'Marketplace',
  '/ligacoes': 'Ligações',
};

function ctx(tab: string, subtab: string, pathname: string, search: string, label: string): NavContext {
  return { tab, subtab, path: pathname + search, label };
}

/** Descreve a localização atual (tab/subtab legível) a partir de pathname+search. */
export function describeLocation(tab: string, pathname: string, search: string): NavContext {
  const sp = new URLSearchParams(search);
  // Detalhes dinâmicos — contexto rico para captura/recomendações.
  if (/^\/eventos\/[^/]+$/.test(pathname)) return ctx(tab, 'detalhe', pathname, search, 'A ver um evento');
  if (/^\/conteudos\/[^/]+$/.test(pathname)) return ctx(tab, 'leitura', pathname, search, 'A ler um conteúdo');
  if (/^\/(perfil|tap\/u)\/[^/]+$/.test(pathname)) return ctx('tap', 'perfil', pathname, search, 'A ver um perfil');
  // Rotas estáticas com nome próprio.
  if (PATH_LABEL[pathname]) return ctx(tab, pathname.replace(/\//g, ''), pathname, search, PATH_LABEL[pathname]);
  // Subtabs (Eventos via ?lista=, Centro via segmento).
  let subtab = sp.get('lista') || sp.get('vista') || '';
  if (tab === 'descobrir' && pathname.startsWith('/descobrir')) {
    subtab = pathname.replace(/^\/descobrir\/?/, '').split('/')[0] || '';
  }
  const label = (TAB_LABEL[tab] || 'Marcianus') + (subtab && SUB_LABEL[subtab] ? ` › ${SUB_LABEL[subtab]}` : '');
  return { tab, subtab, path: pathname + search, label };
}

/** Rótulo de APRESENTAÇÃO da localização atual (traduzido). O `label` do store é contexto para o Hexis (pt, interno). */
import i18next from 'i18next';
export function navDisplayLabel(n: Pick<NavContext, 'tab' | 'label'>): string {
  const tab = i18next.t(`nav.tab.${n.tab}` as never, { ns: 'hexis', defaultValue: '' }) as string;
  return tab || n.label;
}
